import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles, Trash2, User, Bot, X, Volume2, VolumeX, Maximize2, Minimize2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SpellCheckConfirmation from '../chat/SpellCheckConfirmation';
import TypingAnimation from '../chat/TypingAnimation';
import TypingIndicator from '../chat/TypingIndicator';
import ProductRecommendationCard from '../chat/ProductRecommendationCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to check if message contains cart marker
const hasCartMarker = (content) => {
  return content && content.includes('[IR_AL_CARRITO]');
};

// Helper to clean cart marker from message
const cleanCartMarker = (content) => {
  return content ? content.replace('[IR_AL_CARRITO]', '').trim() : content;
};

const GlassmorphismChatPanel = ({ 
  isOpen, 
  onClose, 
  isAIThinking,
  setIsAIThinking,
  onProductsRecommended,
  isMuted,
  onToggleMute
}) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpellChecking, setIsSpellChecking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pendingSpellCheck, setPendingSpellCheck] = useState(null);
  const [products, setProducts] = useState([]);
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Handle navigation to cart
  const handleGoToCart = () => {
    onClose?.(); // Close the chat panel first
    navigate('/cart');
  };

  // Fetch products for recommendations
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        // Silent fail
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    // Generate session ID on mount
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    // Add welcome message with typing animation
    const welcomeId = `welcome-${Date.now()}`;
    setMessages([{
      id: welcomeId,
      role: 'assistant',
      content: '¡Bienvenida al Beauty Studio de AliGlam! ✨ Soy tu asesora de belleza personal. Pregúntame sobre productos, rutinas de skincare, o déjame ayudarte a encontrar el maquillaje perfecto para ti. 💄',
      isTyping: true
    }]);
    setTypingMessageId(welcomeId);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, pendingSpellCheck]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Extract product mentions from AI response with improved matching
  const extractProductMentions = useCallback((text) => {
    if (!products.length) return [];
    
    const lowerText = text.toLowerCase();
    
    // Common brand names to detect
    const knownBrands = [
      'rare beauty', 'nars', 'mac', 'estée lauder', 'estee lauder', 'fenty', 
      'charlotte tilbury', 'dior', 'chanel', 'ysl', 'urban decay', 'too faced',
      'benefit', 'maybelline', 'loreal', "l'oreal", 'elf', 'e.l.f', 'r.e.m',
      'rem beauty', 'ariana grande', 'kylie', 'huda', 'anastasia', 'abh',
      'morphe', 'colourpop', 'clinique', 'bobbi brown', 'laura mercier'
    ];
    
    // Common shade/color keywords
    const shadeKeywords = [
      'grateful', 'behave', 'orgasm', 'deep throat', 'dusty rose', 'nude',
      'pink', 'coral', 'peach', 'berry', 'mauve', 'plum', 'rose', 'blush',
      'natural', 'warm', 'cool', 'light', 'medium', 'deep', 'dark',
      'honey', 'caramel', 'mocha', 'espresso', 'vanilla', 'sand', 'beige',
      'fair', 'porcelain', 'ivory', 'buff', 'nude', 'tan', 'golden'
    ];
    
    // Category keywords for fallback matching
    const categoryKeywords = {
      'labios': ['labial', 'labios', 'lipstick', 'gloss', 'lip', 'labios'],
      'rubores': ['rubor', 'blush', 'mejillas', 'colorete', 'sonrojo'],
      'perfumes': ['perfume', 'fragancia', 'aroma', 'eau de', 'colonia'],
      'bases': ['base', 'foundation', 'maquillaje base', 'cobertura'],
      'correctores': ['corrector', 'concealer'],
      'cejas': ['ceja', 'brow', 'cejas'],
      'polvos': ['polvo', 'powder', 'setting'],
      'primers': ['primer', 'prebase'],
      'sets': ['set', 'kit', 'colección']
    };
    
    // Score products by relevance with weighted priorities
    const scoredProducts = products.map(product => {
      const productName = product.name.toLowerCase();
      const productCategory = product.category?.toLowerCase() || '';
      let score = 0;
      let matchDetails = [];
      
      // PRIORITY 1: Exact or near-exact product name match (+50)
      // Check if the AI mentions this exact product name
      const nameWithoutBrand = productName.replace(/[-–—]/g, ' ').trim();
      const nameWords = nameWithoutBrand.split(/\s+/).filter(w => w.length > 2);
      
      // Check for substantial name match (most words from product name appear in text)
      const matchingNameWords = nameWords.filter(word => lowerText.includes(word));
      const nameMatchRatio = matchingNameWords.length / nameWords.length;
      
      if (nameMatchRatio >= 0.7 && nameWords.length >= 3) {
        // Strong name match - most of the product name words appear
        score += 50;
        matchDetails.push('exact_name');
      } else if (nameMatchRatio >= 0.5 && nameWords.length >= 3) {
        // Partial name match
        score += 30;
        matchDetails.push('partial_name');
      }
      
      // PRIORITY 2: Brand match (+30)
      const productBrand = knownBrands.find(brand => productName.includes(brand));
      if (productBrand && lowerText.includes(productBrand)) {
        score += 30;
        matchDetails.push('brand');
      }
      
      // PRIORITY 3: Shade/Color match (+20)
      const productShade = shadeKeywords.find(shade => productName.includes(shade));
      if (productShade && lowerText.includes(productShade)) {
        score += 20;
        matchDetails.push('shade');
      }
      
      // Also check for shade mentioned after "tono" or "shade" or "color"
      const tonoMatch = lowerText.match(/tono\s+(\w+)/i) || 
                        lowerText.match(/shade\s+(\w+)/i) ||
                        lowerText.match(/color\s+(\w+)/i);
      if (tonoMatch && productName.includes(tonoMatch[1].toLowerCase())) {
        score += 25; // Stronger shade match when explicitly mentioned
        matchDetails.push('explicit_shade');
      }
      
      // PRIORITY 4: Variant keywords (+10)
      const variantKeywords = ['liquid', 'líquido', 'cream', 'crema', 'matte', 'mate', 
                              'glossy', 'satin', 'shimmer', 'dewy', 'soft', 'pinch'];
      const matchingVariants = variantKeywords.filter(v => 
        productName.includes(v) && lowerText.includes(v)
      );
      if (matchingVariants.length > 0) {
        score += 10 * matchingVariants.length;
        matchDetails.push('variant');
      }
      
      // PRIORITY 5: Category match (+10) - fallback
      for (const [cat, keywords] of Object.entries(categoryKeywords)) {
        const categoryMatches = keywords.some(kw => lowerText.includes(kw));
        const productInCategory = productCategory.includes(cat) || cat.includes(productCategory);
        
        if (categoryMatches && productInCategory) {
          score += 10;
          matchDetails.push('category');
          break;
        }
      }
      
      // Bonus: If product has high score from name/brand/shade, boost slightly
      if (score >= 50 && matchDetails.includes('brand')) {
        score += 5; // Bonus for brand + other match
      }
      
      return { product, score, matchDetails };
    }).filter(item => item.score > 0);
    
    // Sort by score descending
    scoredProducts.sort((a, b) => b.score - a.score);
    
    // Return top matches, but prioritize diversity if scores are close
    const topProducts = [];
    const seenCategories = new Set();
    
    for (const item of scoredProducts) {
      // Always include high-scoring products (score >= 50 means strong match)
      if (item.score >= 50 || topProducts.length < 2) {
        topProducts.push(item.product);
        seenCategories.add(item.product.category);
      } else if (topProducts.length < 4 && !seenCategories.has(item.product.category)) {
        // Add variety from different categories for lower scores
        topProducts.push(item.product);
        seenCategories.add(item.product.category);
      }
      
      if (topProducts.length >= 4) break;
    }
    
    return topProducts;
  }, [products]);

  // Perform spell check
  const performSpellCheck = async (message) => {
    try {
      setIsSpellChecking(true);
      const response = await axios.post(`${API}/assistant/spell-check`, {
        message: message
      });
      
      if (response.data.has_corrections) {
        setPendingSpellCheck({
          original: response.data.original,
          corrected: response.data.corrected,
          correctionsSummary: response.data.corrections_summary
        });
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setIsSpellChecking(false);
    }
  };

  // Handle sending message
  const sendMessageToAI = async (messageText) => {
    const msgId = `user-${Date.now()}`;
    const userMessage = messageText.trim();
    
    setMessages(prev => [...prev, { 
      id: msgId,
      role: 'user', 
      content: userMessage 
    }]);
    
    setIsLoading(true);
    setIsAIThinking(true);

    try {
      const response = await fetch(`${API}/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      
      if (data.session_id) setSessionId(data.session_id);
      
      const recommendedProducts = extractProductMentions(data.response);
      
      // Notify parent about recommended products for 3D highlighting
      if (recommendedProducts.length > 0) {
        onProductsRecommended?.(recommendedProducts);
      }
      
      const assistantMsgId = `assistant-${Date.now()}`;
      setMessages(prev => [...prev, { 
        id: assistantMsgId,
        role: 'assistant', 
        content: data.response,
        products: recommendedProducts,
        isTyping: true
      }]);
      setTypingMessageId(assistantMsgId);

    } catch (error) {
      toast.error('Error al enviar mensaje');
      const errorMsgId = `error-${Date.now()}`;
      setMessages(prev => [...prev, { 
        id: errorMsgId,
        role: 'assistant', 
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
        isTyping: false
      }]);
    } finally {
      setIsLoading(false);
      setIsAIThinking(false);
      inputRef.current?.focus();
    }
  };

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading || isSpellChecking) return;

    const cleanMessage = messageText.trim();
    setInputValue('');
    
    const needsCorrection = await performSpellCheck(cleanMessage);
    
    if (!needsCorrection) {
      await sendMessageToAI(cleanMessage);
    }
  };

  const handleSpellCheckConfirm = async (correctedText) => {
    setPendingSpellCheck(null);
    await sendMessageToAI(correctedText);
  };

  const handleSpellCheckEdit = (correctedText) => {
    setPendingSpellCheck(null);
    setInputValue(correctedText);
    inputRef.current?.focus();
  };

  const handleTypingComplete = (msgId) => {
    if (typingMessageId === msgId) {
      setTypingMessageId(null);
      setMessages(prev => 
        prev.map(msg => 
          msg.id === msgId ? { ...msg, isTyping: false } : msg
        )
      );
    }
  };

  const handleClearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API}/assistant/session/${sessionId}`, { method: 'DELETE' });
      } catch (error) {
        // Silent fail
      }
    }
    
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setPendingSpellCheck(null);
    
    const welcomeId = `welcome-${Date.now()}`;
    setMessages([{
      id: welcomeId,
      role: 'assistant',
      content: '¡Nueva conversación iniciada! ✨ ¿En qué puedo ayudarte? 💄',
      isTyping: true
    }]);
    setTypingMessageId(welcomeId);
    
    toast.success('Conversación reiniciada');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed right-4 top-24 z-50 transition-all duration-500 ease-out transform ${
        isExpanded ? 'w-[450px] h-[calc(100vh-120px)]' : 'w-[380px] h-[520px]'
      }`}
      data-testid="glassmorphism-chat-panel"
      style={{
        animation: 'slideInRight 0.4s ease-out'
      }}
    >
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .message-enter {
          animation: fadeInUp 0.3s ease-out;
        }
      `}</style>
      <div className="w-full h-full bg-white/85 backdrop-blur-2xl rounded-3xl shadow-[0_20px_60px_rgba(186,123,124,0.2),0_8px_24px_rgba(0,0,0,0.1)] border border-white/60 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-[#BA7B7C]/15 via-[#D4A5A5]/10 to-[#FFD4E5]/10 border-b border-[#E7E5E4]/40 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 bg-gradient-to-br from-[#D4A5A5] to-[#BA7B7C] rounded-2xl flex items-center justify-center shadow-lg shadow-[#BA7B7C]/30 transform hover:scale-105 transition-transform">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 
                className="text-base font-semibold text-[#1C1917]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Beauty Advisor
              </h3>
              <p className="text-xs text-[#A8A29E] flex items-center space-x-1.5">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <span>En línea</span>
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={onToggleMute}
              className="p-2.5 text-[#A8A29E] hover:text-[#BA7B7C] hover:bg-[#BA7B7C]/10 rounded-xl transition-all duration-200 hover:scale-105"
              title={isMuted ? "Activar sonido" : "Silenciar"}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2.5 text-[#A8A29E] hover:text-[#BA7B7C] hover:bg-[#BA7B7C]/10 rounded-xl transition-all duration-200 hover:scale-105"
              title={isExpanded ? "Reducir" : "Expandir"}
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={handleClearChat}
              className="p-2.5 text-[#A8A29E] hover:text-[#BA7B7C] hover:bg-[#BA7B7C]/10 rounded-xl transition-all duration-200 hover:scale-105"
              title="Nueva conversación"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2.5 text-[#A8A29E] hover:text-red-400 hover:bg-red-50 rounded-xl transition-all duration-200 hover:scale-105"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth" data-testid="chat-messages-3d">
          {messages.map((message, index) => (
            <div key={message.id} className="message-enter" style={{ animationDelay: `${index * 0.05}s` }}>
              <div className={`flex items-start space-x-3 ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <div className={`w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md transition-transform hover:scale-105 ${
                  message.role === 'user' 
                    ? 'bg-gradient-to-br from-[#F5F0EB] to-[#E7E5E4] text-[#57534E]' 
                    : 'bg-gradient-to-br from-[#D4A5A5] to-[#BA7B7C] text-white'
                }`}>
                  {message.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`max-w-[82%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-4 py-3 rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-[#1C1917] to-[#3F3F46] text-white rounded-br-md'
                      : 'bg-white/95 text-[#1C1917] rounded-bl-md border border-[#E7E5E4]/60'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                      {message.isTyping && typingMessageId === message.id ? (
                        <TypingAnimation 
                          content={cleanCartMarker(message.content)} 
                          onComplete={() => handleTypingComplete(message.id)}
                          speed={10}
                        />
                      ) : (
                        cleanCartMarker(message.content)
                      )}
                    </p>
                  </div>
                  
                  {/* Go to Cart Button - shown when AI mentions shopping/payment */}
                  {message.role === 'assistant' && hasCartMarker(message.content) && !message.isTyping && (
                    <div className="mt-3 message-enter" style={{ animationDelay: '0.3s' }}>
                      <button
                        onClick={handleGoToCart}
                        data-testid="go-to-cart-btn"
                        className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-[#D4A5A5] to-[#BA7B7C] text-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 font-medium text-sm"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Ir al Carrito</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Product Recommendations */}
              {message.products && message.products.length > 0 && !message.isTyping && (
                <div className="mt-4 ml-12 message-enter" style={{ animationDelay: '0.2s' }}>
                  <p className="text-xs text-[#A8A29E] mb-3 flex items-center space-x-1.5">
                    <Sparkles className="w-3 h-3 text-[#BA7B7C]" />
                    <span>Productos recomendados:</span>
                  </p>
                  <div className="space-y-2.5">
                    {message.products.slice(0, 2).map((product, pIndex) => (
                      <div key={product.id} style={{ animationDelay: `${0.3 + pIndex * 0.1}s` }} className="message-enter">
                        <ProductRecommendationCard 
                          product={product} 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Spell Check Confirmation */}
          {pendingSpellCheck && (
            <div className="ml-12 message-enter">
              <SpellCheckConfirmation
                original={pendingSpellCheck.original}
                corrected={pendingSpellCheck.corrected}
                correctionsSummary={pendingSpellCheck.correctionsSummary}
                onConfirm={handleSpellCheckConfirm}
                onEdit={handleSpellCheckEdit}
              />
            </div>
          )}
          
          {/* Typing Indicator */}
          {(isLoading || isSpellChecking) && !pendingSpellCheck && (
            <div className="message-enter">
              <TypingIndicator />
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-[#E7E5E4]/40 p-4 bg-gradient-to-r from-white/60 via-[#FDF8F6]/60 to-[#FFD4E5]/20">
          <div className="flex items-center space-x-3">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu pregunta..."
              className="flex-1 px-4 py-3 rounded-2xl border border-[#E7E5E4] bg-white/90 focus:outline-none focus:border-[#BA7B7C] focus:ring-3 focus:ring-[#BA7B7C]/20 transition-all duration-200 text-sm shadow-sm hover:shadow-md"
              disabled={isLoading || isSpellChecking || !!pendingSpellCheck}
              data-testid="chat-input-3d"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={isLoading || isSpellChecking || !inputValue.trim() || !!pendingSpellCheck}
              className="w-11 h-11 bg-gradient-to-br from-[#D4A5A5] to-[#BA7B7C] text-white rounded-2xl flex items-center justify-center shadow-lg shadow-[#BA7B7C]/30 hover:shadow-xl hover:shadow-[#BA7B7C]/40 hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none"
              data-testid="send-btn-3d"
            >
              {isLoading || isSpellChecking ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlassmorphismChatPanel;
